import { useState } from 'react';
import { Route, Clock, BookOpen, CircleCheck as CheckCircle2, ChevronRight, Sparkles, Loader as Loader2, CircleAlert as AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import type { CraftLearningPath, CraftModuleRecord, UserCraftProgress } from '../../../types/craft';

interface Props {
  paths: CraftLearningPath[];
  modules: CraftModuleRecord[];
  progress: UserCraftProgress[];
  onSelectModule: (slug: string) => void;
}

const STATUS_CONFIG = {
  completed: { color: 'text-app-positive', icon: CheckCircle2, label: 'Ready' },
  processing: { color: 'text-app-warning', icon: Loader2, label: 'Generating...' },
  error: { color: 'text-app-critical', icon: AlertCircle, label: 'Error' },
  none: { color: 'text-app-muted', icon: Sparkles, label: 'Awaiting generation' },
};

export default function CraftLearningPaths({ paths, modules, progress, onSelectModule }: Props) {
  const [expandedPath, setExpandedPath] = useState<string | null>(
    paths.length > 0 ? paths[0].slug : null
  );

  if (!paths.length) {
    return (
      <div className="text-center py-16">
        <Route className="w-10 h-10 text-app-line-strong mx-auto mb-3" />
        <p className="text-sm text-app-muted font-medium mb-1">No Learning Paths Available</p>
        <p className="text-xs text-app-muted">Learning paths will appear here once generated.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-app-surface rounded-lg border border-app-line p-8 mb-8">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 mb-3">
            <Route className="w-5 h-5 text-app-focus" />
            <span className="text-xs font-semibold uppercase tracking-wider text-app-focus">Guided Learning</span>
          </div>
          <h2 className="text-xl font-bold text-app-ink mb-2">Learning Paths</h2>
          <p className="text-sm text-app-muted leading-relaxed">
            Curated sequences of modules designed for specific roles and goals.
            Each path provides a structured progression from foundational concepts to advanced mastery.
          </p>
          <div className="flex items-center gap-4 mt-3 text-xs text-app-muted">
            <span className="flex items-center gap-1">
              <Route className="w-3.5 h-3.5" />
              {paths.length} paths
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {paths.map(path => {
          const isExpanded = expandedPath === path.slug;
          const sequence = (path.module_sequence || []) as { module_slug: string; rationale: string }[];
          const totalModules = sequence.length;

          const completedModules = sequence.filter(s => {
            const modProgress = progress.filter(p => p.module_slug === s.module_slug && p.status === 'completed');
            return modProgress.length >= 10;
          }).length;

          return (
            <div key={path.slug} className="border border-app-line rounded-lg overflow-hidden bg-app-surface">
              <button
                onClick={() => setExpandedPath(isExpanded ? null : path.slug)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-app-canvas transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-app-focus/10 flex items-center justify-center flex-shrink-0">
                    <Route className="w-4.5 h-4.5 text-app-focus" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-semibold text-app-ink">{path.title}</h3>
                    <div className="flex items-center gap-3 text-xs text-app-muted mt-0.5">
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-3 h-3" />
                        {totalModules} modules
                      </span>
                      {path.estimated_total_minutes > 0 && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {Math.round(path.estimated_total_minutes / 60)}h
                        </span>
                      )}
                      {completedModules > 0 && (
                        <span className="flex items-center gap-1 text-app-positive">
                          <CheckCircle2 className="w-3 h-3" />
                          {completedModules}/{totalModules}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-app-muted" /> : <ChevronDown className="w-4 h-4 text-app-muted" />}
              </button>

              {isExpanded && (
                <div className="px-5 pb-5 border-t border-app-line">
                  {path.description && (
                    <p className="text-xs text-app-muted pt-3 pb-2">{path.description}</p>
                  )}
                  {path.intended_audience && (
                    <p className="text-xs text-app-muted pb-2"><span className="font-medium text-app-muted">Audience:</span> {path.intended_audience}</p>
                  )}
                  {path.learning_objective && (
                    <p className="text-xs text-app-muted pb-4"><span className="font-medium text-app-muted">Objective:</span> {path.learning_objective}</p>
                  )}

                  {completedModules > 0 && totalModules > 0 && (
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex-1 h-1.5 bg-app-line rounded-full overflow-hidden" role="progressbar" aria-label={`${path.title} completion`} aria-valuemin={0} aria-valuemax={totalModules} aria-valuenow={completedModules}>
                        <div
                          className="h-full bg-app-positive rounded-full transition"
                          style={{ width: `${(completedModules / totalModules) * 100}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-app-muted">{completedModules}/{totalModules}</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    {sequence.map((step, idx) => {
                      const mod = modules.find(m => m.slug === step.module_slug);
                      if (!mod) {
                        return (
                          <div key={idx} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-app-canvas opacity-50">
                            <span className="w-6 h-6 rounded-full bg-app-line text-app-muted flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                              {idx + 1}
                            </span>
                            <span className="text-xs text-app-muted">{step.module_slug} (not found)</span>
                          </div>
                        );
                      }

                      const modProgress = progress.filter(p => p.module_slug === mod.slug && p.status === 'completed');
                      const modCompleted = modProgress.length >= 10;
                      const isReady = mod.status === 'completed';
                      const statusCfg = STATUS_CONFIG[mod.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.none;
                      const StatusIcon = statusCfg.icon;

                      return (
                        <div
                          key={idx}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition group ${
                            isReady
                              ? 'border-app-line hover:border-app-focus/30 hover:shadow-none cursor-pointer'
                              : 'border-app-line bg-app-canvas/50'
                          }`}
                          onClick={() => isReady && onSelectModule(mod.slug)}
                        >
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                            modCompleted
                              ? 'bg-app-positive/10 text-app-positive'
                              : 'bg-app-canvas text-app-muted'
                          }`}>
                            {modCompleted ? <CheckCircle2 className="w-3.5 h-3.5" /> : idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-medium truncate ${isReady ? 'text-app-ink group-hover:text-app-focus' : 'text-app-muted'}`}>
                              {mod.title}
                            </p>
                            {step.rationale && (
                              <p className="text-[10px] text-app-muted truncate">{step.rationale}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <StatusIcon className={`w-3 h-3 ${statusCfg.color} ${mod.status === 'processing' ? 'animate-spin' : ''}`} />
                            {isReady && (
                              <ChevronRight className="w-3.5 h-3.5 text-app-line-strong group-hover:text-app-focus transition-colors" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
